import type { FleetGeometry } from "../lib/geometry";
import { isHighlighted } from "../lib/selection";
import type { Selection } from "../lib/selection";
import type { TopologyPayload } from "../lib/types/topology";
import type { ViewMode } from "../lib/viewMode";

import {
  BoxNode, ConnectorLine, boxToSelection, boxToTarget, makeEndpointCheck,
} from "./TopTierRails";


interface PortRailsProps {
  geometry: FleetGeometry;
  topology: TopologyPayload;
  selection?: Selection;
  onSelect?: (s: Selection | undefined) => void;
  /** Double-click = select AND jump the LOPA to the highlighted seats. */
  onActivate?: (s: Selection) => void;
  viewMode: ViewMode;
}


export default function PortRails({
  geometry, topology, selection, onSelect, onActivate, viewMode,
}: PortRailsProps) {
  // eX1 ports hang off MCU boxes → MCU view only. eX3 ports hang off NC
  // boxes (data tier) → only Data view exists for eX3 anyway.
  const isEx3 = topology.layout.topology.type === "nc-port";
  const visible = isEx3 ? viewMode !== "mcu" : viewMode === "mcu";  // avail = data head-end
  if (!visible) return null;
  const portConnectors = geometry.connectors.filter((c) => c.conn === "port");
  const hasSelection = Boolean(selection);
  const endpointsBright = makeEndpointCheck(geometry, selection, topology, viewMode);

  return (
    <g>
      {portConnectors.map((c, i) => {
        const bright =
          isHighlighted(
            selection,
            { kind: "connector", conn: c.conn, nc: c.nc, port: c.port },
            topology,
          ) && endpointsBright(c);
        return (
          <ConnectorLine
            key={`pr-conn-${i}`}
            c={c}
            opacity={bright ? (c.opacity ?? 1) : 0.06}
            emphasised={hasSelection && bright}
          />
        );
      })}
      {geometry.portBoxes.map((b, i) => {
        const bright = isHighlighted(selection, boxToTarget(b), topology);
        return (
          <BoxNode
            key={`port-${i}`}
            b={b}
            opacity={bright ? 1 : 0.15}
            emphasised={hasSelection && bright}
            onClick={onSelect ? () => onSelect(boxToSelection(b)) : undefined}
            onDoubleClick={onActivate
              ? () => onActivate(boxToSelection(b))
              : undefined}
          />
        );
      })}
    </g>
  );
}
